import React, { useState, useEffect } from 'react';
import { Save, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from '../../hooks/use-toast';

const ContactInfoManager = () => {
  const [contactInfo, setContactInfo] = useState({
    title: '',
    description: '',
    email: '',
    phone: '',
    address: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  useEffect(() => {
    fetchContactInfo();
  }, []);

  const fetchContactInfo = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/contact-info`);
      if (response.ok) {
        const data = await response.json();
        setContactInfo(data);
      }
    } catch (error) {
      console.error('Error fetching contact info:', error);
      toast({
        title: 'Error',
        description: 'Failed to load contact information',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/contact-info`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(contactInfo),
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'Contact information updated successfully',
        });
      } else {
        throw new Error('Failed to update');
      }
    } catch (error) {
      console.error('Error saving contact info:', error);
      toast({
        title: 'Error',
        description: 'Failed to update contact information',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    setContactInfo({
      ...contactInfo,
      [e.target.name]: e.target.value,
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="border-2 border-red-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-xl shadow">
        <div className="bg-gradient-to-r from-red-50 to-gray-50 dark:from-gray-700 dark:to-gray-700 rounded-t-xl px-6 py-4">
          <h3 className="text-lg font-semibold text-red-700 dark:text-red-400">Contact Page Settings</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Manage the contact information displayed to users
          </p>
        </div>
        <div className="pt-6 px-6 pb-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Page Title
            </label>
            <Input
              name="title"
              value={contactInfo.title}
              onChange={handleChange}
              placeholder="Contact Admin"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Description
            </label>
            <Textarea
              name="description"
              value={contactInfo.description}
              onChange={handleChange}
              placeholder="Have a question or need support?"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email
              </label>
              <Input
                type="email"
                name="email"
                value={contactInfo.email}
                onChange={handleChange}
                placeholder="welcome@trainerlist.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Phone
              </label>
              <Input
                type="tel"
                name="phone"
                value={contactInfo.phone}
                onChange={handleChange}
                placeholder="+1 (555) 123-4567"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Address
            </label>
            <Textarea
              name="address"
              value={contactInfo.address}
              onChange={handleChange}
              placeholder="123 Fitness Street, Toronto, ON M5V 1A1"
              rows={2}
            />
          </div>

          <div className="flex justify-end pt-4">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-gradient-to-r from-red-600 to-black hover:from-red-700 hover:to-gray-900"
            >
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      <Card className="border-2 border-blue-200 dark:border-gray-700 dark:bg-gray-800">
        <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20">
          <CardTitle className="text-blue-700 dark:text-blue-400">Preview</CardTitle>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            How it appears to users
          </p>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{contactInfo.title}</h3>
              <p className="text-gray-600 dark:text-gray-400 mt-2">{contactInfo.description}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4">
              <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <p className="font-semibold text-red-700 dark:text-red-400">Email</p>
                <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">{contactInfo.email}</p>
              </div>
              <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <p className="font-semibold text-red-700 dark:text-red-400">Phone</p>
                <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">{contactInfo.phone}</p>
              </div>
              <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <p className="font-semibold text-red-700 dark:text-red-400">Address</p>
                <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">{contactInfo.address}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ContactInfoManager;
